/*
 * The command menu (Ctrl K, or the top search): find a client, job or visit by name, phone or address, or jump to a
 * quick action. Arrow keys move, Enter opens, Escape closes.
 */
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearch } from "../api/hooks";
import { useSession } from "../app/session";
import { clock, shortDate } from "./format";
import { Icon, type IconName } from "./Icon";

interface Item {
  key: string;
  icon: IconName;
  label: string;
  hint: string;
  go: () => void;
}

export function CommandMenu({ open, onClose, start = "" }: { open: boolean; onClose: () => void; start?: string }) {
  const { company, can } = useSession();
  const navigate = useNavigate();
  const [q, setQ] = useState(start);
  const [at, setAt] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const found = useSearch(company.id, open ? q : "");

  useEffect(() => {
    if (!open) return;
    setQ(start);
    setAt(0);
    input.current?.focus();
  }, [open, start]);

  const go = (fn: () => unknown) => () => {
    onClose();
    void fn();
  };
  const actions: Item[] = [
    ...(can("client.update")
      ? [
          {
            key: "a-client",
            icon: "plus" as const,
            label: "New client",
            hint: "Create",
            go: go(() => navigate({ to: "/clients/new" })),
          },
        ]
      : []),
    ...(can("job.update")
      ? [
          {
            key: "a-job",
            icon: "plus" as const,
            label: "New job",
            hint: "Create",
            go: go(() => navigate({ to: "/jobs/new", search: {} })),
          },
        ]
      : []),
    ...(can("schedule.read")
      ? [
          {
            key: "a-sched",
            icon: "schedule" as const,
            label: "Schedule",
            hint: "Go to",
            go: go(() => navigate({ to: "/schedule" })),
          },
        ]
      : []),
    ...(can("client.read")
      ? [
          {
            key: "a-clients",
            icon: "clients" as const,
            label: "Clients",
            hint: "Go to",
            go: go(() => navigate({ to: "/clients" })),
          },
        ]
      : []),
    ...(can("job.read")
      ? [{ key: "a-jobs", icon: "jobs" as const, label: "Jobs", hint: "Go to", go: go(() => navigate({ to: "/jobs" })) }]
      : []),
  ];
  const typed = q.trim().length >= 2;
  const d = typed ? found.data : undefined;
  const items: Item[] = typed
    ? [
        ...(d?.clients ?? []).map((c) => ({
          key: `c${c.id}`,
          icon: "clients" as const,
          label: c.name,
          hint: [c.active ? null : "Not active", c.phone, c.address].filter(Boolean).join(" · ") || "Client",
          go: go(() => navigate({ to: "/clients/$id", params: { id: c.id } })),
        })),
        ...(d?.jobs ?? []).map((j) => ({
          key: `j${j.id}`,
          icon: "jobs" as const,
          label: `${j.name}${j.number ? ` #${String(j.number)}` : ""}`,
          hint: j.clientName,
          go: go(() => navigate({ to: "/jobs/$id", params: { id: j.id } })),
        })),
        ...(d?.visits ?? []).map((v) => ({
          key: `v${v.ref}`,
          icon: "schedule" as const,
          label: `${v.clientName} on ${shortDate(v.date)}`,
          hint: `${clock(v.startTime)} · ${v.jobName}`,
          go: go(() => navigate({ to: "/visits/$ref", params: { ref: v.ref } })),
        })),
        ...actions.filter((a) => a.label.toLowerCase().includes(q.trim().toLowerCase())),
      ]
    : actions;
  const pick = Math.min(at, Math.max(0, items.length - 1));

  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-i="${String(pick)}"]`)?.scrollIntoView({ block: "nearest" });
  }, [pick]);

  if (!open) return null;
  return createPortal(
    <>
      <div className="scrim" onClick={onClose} />
      <div className="command" role="dialog" aria-modal="true" aria-label="Search">
        <label className="command-input">
          <Icon name="search" size={18} />
          <input
            ref={input}
            value={q}
            placeholder="Find a client, job or visit"
            aria-label="Search"
            aria-controls="command-list"
            aria-activedescendant={items[pick] ? `cmd-${items[pick].key}` : undefined}
            onChange={(e) => {
              setQ(e.target.value);
              setAt(0);
            }}
            onKeyDown={(e) => {
              const n = Math.max(1, items.length);
              if (e.key === "Escape") onClose();
              else if (e.key === "Enter") items[pick]?.go();
              else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                e.preventDefault();
                setAt((pick + (e.key === "ArrowDown" ? 1 : n - 1)) % n);
              }
            }}
          />
          <kbd>Esc</kbd>
        </label>
        <div className="command-list" id="command-list" role="listbox" ref={list}>
          {!typed ? <div className="menu-label">Quick actions</div> : null}
          {items.map((i, n) => (
            <button
              key={i.key}
              id={`cmd-${i.key}`}
              type="button"
              role="option"
              aria-selected={n === pick}
              data-i={n}
              onMouseMove={() => setAt(n)}
              onClick={i.go}
            >
              <Icon name={i.icon} size={18} />
              <b>{i.label}</b>
              <span className="muted">{i.hint}</span>
            </button>
          ))}
          {typed && found.isFetching && !d ? <div className="command-empty muted">Looking</div> : null}
          {typed && d && !items.length ? (
            <div className="command-empty muted">Nothing found for "{q.trim()}". Check the spelling or try a phone number.</div>
          ) : null}
          {found.error ? <div className="command-empty muted">Search didn't load. Try again.</div> : null}
        </div>
      </div>
    </>,
    document.body,
  );
}
